/**
 * Minimal self-contained semver subset (架构方案 §5.1 自研轻量, §5.5 版本协商).
 *
 * The platform deliberately avoids a third-party `semver` dependency in this
 * zero-platform-dependency `common` layer (see docs/01 C.2 supply-chain notes).
 * Only the range operators the manifest contract needs are supported:
 *   - exact: `1.2.3`
 *   - caret: `^1.2.3`  (>=1.2.3 <2.0.0; for 0.x.y caret pins the minor)
 *   - tilde: `~1.2.3`  (>=1.2.3 <1.3.0)
 *   - wildcard: `*`, `x`, `1.x`, `1.2.*`
 */

export interface SemVer {
  major: number;
  minor: number;
  patch: number;
}

/**
 * Parse `major.minor.patch`, tolerating shorter forms like `1` or `1.0` and a
 * leading `v`. Pre-release / build metadata is ignored. Returns `undefined`
 * when the string is not a recognisable version.
 */
export function parseVersion(input: string): SemVer | undefined {
  if (typeof input !== 'string') {
    return undefined;
  }
  const cleaned = input.trim().replace(/^v/i, '');
  const core = cleaned.split(/[-+]/)[0];
  const parts = core.split('.');
  if (parts.length === 0 || parts.length > 3) {
    return undefined;
  }
  const nums: number[] = [];
  for (const part of parts) {
    if (!/^\d+$/.test(part)) {
      return undefined;
    }
    nums.push(Number(part));
  }
  return { major: nums[0], minor: nums[1] ?? 0, patch: nums[2] ?? 0 };
}

function compare(a: SemVer, b: SemVer): number {
  if (a.major !== b.major) {
    return a.major - b.major;
  }
  if (a.minor !== b.minor) {
    return a.minor - b.minor;
  }
  return a.patch - b.patch;
}

/**
 * Compare two version strings.
 *
 * Positive when `left` is newer, negative when it is older, `0` when they name
 * the same release. `undefined` when either string is not a version: callers
 * that already hold a package keep it rather than guessing which one is newer.
 */
export function compareVersions(
  left: string,
  right: string,
): number | undefined {
  const a = parseVersion(left);
  const b = parseVersion(right);
  if (!a || !b) {
    return undefined;
  }
  return compare(a, b);
}

function gte(a: SemVer, b: SemVer): boolean {
  return compare(a, b) >= 0;
}

function lt(a: SemVer, b: SemVer): boolean {
  return compare(a, b) < 0;
}

function isWildcardPart(part: string): boolean {
  return part === '*' || part === 'x' || part === 'X';
}

/**
 * Returns true when `version` satisfies `range`. Unknown/malformed ranges or
 * versions resolve to `false` (fail closed → caller degrades, 架构方案 §5.5).
 */
export function satisfies(version: string, range: string): boolean {
  const v = parseVersion(version);
  if (!v || typeof range !== 'string') {
    return false;
  }
  const trimmed = range.trim();

  // Full wildcard: any version matches.
  if (trimmed === '' || trimmed === '*' || trimmed === 'x' || trimmed === 'X') {
    return true;
  }

  if (trimmed.startsWith('^')) {
    return satisfiesCaret(v, trimmed.slice(1));
  }
  if (trimmed.startsWith('~')) {
    return satisfiesTilde(v, trimmed.slice(1));
  }

  return satisfiesPlain(v, trimmed);
}

function satisfiesCaret(v: SemVer, base: string): boolean {
  const b = parseVersion(base);
  if (!b) {
    return false;
  }
  // Caret keeps the left-most non-zero component fixed.
  let upper: SemVer;
  if (b.major > 0) {
    upper = { major: b.major + 1, minor: 0, patch: 0 };
  } else if (b.minor > 0) {
    upper = { major: 0, minor: b.minor + 1, patch: 0 };
  } else {
    upper = { major: 0, minor: 0, patch: b.patch + 1 };
  }
  return gte(v, b) && lt(v, upper);
}

function satisfiesTilde(v: SemVer, base: string): boolean {
  const b = parseVersion(base);
  if (!b) {
    return false;
  }
  const upper: SemVer = { major: b.major, minor: b.minor + 1, patch: 0 };
  return gte(v, b) && lt(v, upper);
}

function satisfiesPlain(v: SemVer, range: string): boolean {
  const parts = range.split('.');
  if (parts.length === 0 || parts.length > 3) {
    return false;
  }
  const keys: (keyof SemVer)[] = ['major', 'minor', 'patch'];
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];
    if (isWildcardPart(part)) {
      // A wildcard segment matches anything from here on.
      return true;
    }
    if (!/^\d+$/.test(part)) {
      return false;
    }
    if (Number(part) !== v[keys[i]]) {
      return false;
    }
  }
  // Shorter exact ranges (e.g. `1.2`) only constrain the supplied segments.
  return true;
}
